import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { io } from 'socket.io-client'
import { HOST } from '../constants'
import { UserContext } from './context'
import APINOTIFICACIONES from '../providers/api.notificaciones'

export const NotificacionesContext = createContext({
  notificaciones: [],
  totalNoLeidas: 0,
  conectado: false,
  ultimaNueva: null,
  marcarLeida: () => {},
  marcarTodasLeidas: () => {}
})

// Una sola conexión de Socket.IO por sesión (ver docs/frontend/notificaciones-socket.md del backend)
export const NotificacionesProvider = ({ children }) => {
  const { isAuth } = useContext(UserContext)
  const [notificaciones, setNotificaciones] = useState([])
  const [totalNoLeidas, setTotalNoLeidas] = useState(0)
  const [conectado, setConectado] = useState(false)
  // Última notificación recibida en tiempo real; el navbar la usa para mostrar el aviso emergente
  const [ultimaNueva, setUltimaNueva] = useState(null)

  const cargar = useCallback(() => {
    APINOTIFICACIONES.listarNotificaciones(50)
      .then(response => response.json())
      .then(dataJson => {
        if (dataJson.success) {
          setNotificaciones(dataJson.data.notificaciones || [])
          setTotalNoLeidas(dataJson.data.total_no_leidas || 0)
        }
      })
      .catch(error => console.log('error =>', error))
  }, [])

  useEffect(() => {
    if (!isAuth) {
      setNotificaciones([])
      setTotalNoLeidas(0)
      setConectado(false)
      setUltimaNueva(null)
      return
    }

    cargar()

    const socket = io(HOST, {
      auth: { token: isAuth },
      transports: ['websocket', 'polling']
    })

    socket.on('connect', () => setConectado(true))
    socket.on('disconnect', () => setConectado(false))

    socket.on('connect_error', (err) => {
      setConectado(false)
      // 'Token no válido' | 'Token expirado': el servidor rechaza y Socket.IO no reintenta con el mismo token
      console.warn('Socket:', err.message)
    })

    // Tras una caída de conexión se recarga el listado para recuperar lo que llegó mientras tanto
    socket.io.on('reconnect', cargar)

    socket.on('notificacion:nueva', (notificacion) => {
      setNotificaciones(prev => {
        if (prev.some(n => n.id === notificacion.id)) return prev
        return [notificacion, ...prev]
      })
      if (!notificacion.leida) {
        setTotalNoLeidas(prev => prev + 1)
        setUltimaNueva(notificacion)
      }
    })

    socket.on('notificacion:leida', ({ id, total_no_leidas }) => {
      setNotificaciones(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n))
      setTotalNoLeidas(total_no_leidas)
    })

    socket.on('notificaciones:todas_leidas', ({ total_no_leidas }) => {
      setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })))
      setTotalNoLeidas(total_no_leidas || 0)
    })

    return () => {
      socket.io.off('reconnect', cargar)
      socket.disconnect()
      setConectado(false)
    }
  }, [isAuth, cargar])

  // Actualización optimista; el evento 'notificacion:leida' del socket deja el contador con el valor real
  const marcarLeida = (notificacion) => {
    if (!notificacion || notificacion.leida) return
    setNotificaciones(prev => prev.map(n => n.id === notificacion.id ? { ...n, leida: true } : n))
    setTotalNoLeidas(prev => Math.max(prev - 1, 0))
    APINOTIFICACIONES.marcarNotificacionLeida(notificacion.id)
      .then(response => response.json())
      .then(dataJson => {
        if (dataJson.success) setTotalNoLeidas(dataJson.data.total_no_leidas)
      })
      .catch(error => console.log('error =>', error))
  }

  const marcarTodasLeidas = () => {
    setNotificaciones(prev => prev.map(n => ({ ...n, leida: true })))
    setTotalNoLeidas(0)
    APINOTIFICACIONES.marcarTodasLeidas()
      .then(response => response.json())
      .then(dataJson => {
        if (dataJson.success) setTotalNoLeidas(dataJson.data.total_no_leidas)
      })
      .catch(error => console.log('error =>', error))
  }

  const value = { notificaciones, totalNoLeidas, conectado, ultimaNueva, marcarLeida, marcarTodasLeidas }

  return (
    <NotificacionesContext.Provider value={value}>
      {children}
    </NotificacionesContext.Provider>
  )
}
